/**
 * Configuration for the AI assistant backend + media CDN.
 *
 * Two independent env-derived URLs:
 *
 *   NEXT_PUBLIC_ASSISTANT_BASE_URL  — backend API + WebSocket origin.
 *                                     REQUIRED; no in-code default. See
 *                                     getAssistantBaseUrl below.
 *
 *   NEXT_PUBLIC_MEDIA_CDN_BASE_URL  — base for our owned media CloudFront
 *                                     distribution, INCLUDING the locale segment
 *                                     (e.g., `https://<dist>.cloudfront.net/us`).
 *                                     Required in production builds; the gate in
 *                                     next.config.mjs aborts the build if missing.
 *                                     `getMediaUrl(type, file)` appends `/media/<type>/<file>`.
 *
 * Local dev: media falls through to `/media/<type>/<file>` (served from
 * public/media/). NEXT_PUBLIC_* values are inlined at build time, so client
 * + server see the same value.
 */

import type { MediaType } from '@/types/media';
import { MEDIA_CDN_FALLBACK_URL } from './constants/media-cdn';

/**
 * Get the assistant backend base URL (HTTP).
 * Used for REST API calls (e.g., /api/conversations, /api/transcribe).
 *
 * `NEXT_PUBLIC_ASSISTANT_BASE_URL` is the ONLY source. It is set per-branch
 * by the deploy pipeline and inlined by Next.js at build time.
 *
 * The in-code fallback that used to back this up was deleted on 2026-08-04.
 * It existed because an earlier inspection concluded the deploy build never
 * inlined `NEXT_PUBLIC_*` into the client bundle. That is no longer true —
 * verified by reading the deployed bundles, where the env var name appears
 * zero times and the correct per-environment URL appears as a literal:
 *
 *   test7 (qa):            "https://us-ai-assistant-qa.aolf.app"
 *   www.artofliving.org:   "https://us-ai-assistant.aolf.app"
 *
 * Keeping the fallback was actively harmful, not merely redundant: it was
 * the ONLY file differing between `main` and `qa`, so every merge between
 * them fought over one line, and a clean merge could silently point an
 * environment at the wrong backend. Configuration belongs in configuration;
 * the code is now identical on both branches.
 *
 * @throws if the env var is missing — deliberately loud. Returning '' here
 * would produce URLs like `/api/conversations` against the page's own
 * origin, and a `ws://` with no host, both of which fail far from the cause.
 */
export function getAssistantBaseUrl(): string {
  const envBase = process.env.NEXT_PUBLIC_ASSISTANT_BASE_URL?.trim().replace(
    /\/$/,
    ''
  );
  if (!envBase) {
    throw new Error(
      'NEXT_PUBLIC_ASSISTANT_BASE_URL is not set. It must be configured at ' +
        'the deploy target (per-branch) and, for local development, in ' +
        '.env.local — e.g. https://us-ai-assistant-qa.aolf.app. Next.js ' +
        'inlines NEXT_PUBLIC_* at BUILD time, so a rebuild is required after ' +
        'changing it.'
    );
  }
  return envBase;
}

/**
 * Get the assistant WebSocket URL for a given session ID.
 * Converts http(s):// to ws(s):// automatically.
 */
export function getAssistantWsUrl(sessionId: string): string {
  const base = getAssistantBaseUrl();
  const wsBase = base
    .replace(/^https:\/\//, 'wss://')
    .replace(/^http:\/\//, 'ws://');
  return `${wsBase}/ws/${sessionId}`;
}

/**
 * Get the assistant API URL for a given path.
 *
 * Always returns an absolute URL so the browser hits the backend directly.
 * The earlier "return relative paths in production and let next.config.mjs
 * rewrites proxy them" approach silently broke on static-export deploys
 * (e.g., test7.artofliving.org): rewrites only run when Next.js itself
 * serves the request — under nginx + static export, `/api/*` 404s at
 * nginx before ever reaching the backend.
 *
 * Cross-origin is fine: the backend allows `*.artofliving.org`,
 * `*.cloudfront.net`, and `localhost` via CORS regex (see
 * aol-us-ai-asst/backend/main.py CORSMiddleware).
 */
export function getAssistantApiUrl(path: string): string {
  const base = getAssistantBaseUrl();
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${base}${cleanPath}`;
}

/**
 * Build an absolute URL to a media asset on our owned CloudFront distribution.
 * Pattern: `<base>/media/<type>/<file>`.
 *
 * Resolution order:
 *   1. NEXT_PUBLIC_MEDIA_CDN_BASE_URL env var (preferred — set per-branch by
 *      the deploy pipeline).
 *   2. MEDIA_CDN_FALLBACK_URL constant (TEMPORARY shim — see
 *      `src/lib/constants/media-cdn.ts` and docs/TODO.md). Provides the QA CDN
 *      URL so the build succeeds while the deploy pipeline's env-var setup
 *      is being sorted out.
 *   3. Local dev `/media/<type>/<file>` from `public/`, when neither (1) nor
 *      (2) yields a value AND we are NOT in production.
 *
 * `file` is appended verbatim — caller controls extension + name. No URL
 * encoding applied; pass already-safe paths.
 */
export function getMediaUrl(type: MediaType, file: string): string {
  const envBase = process.env.NEXT_PUBLIC_MEDIA_CDN_BASE_URL?.trim().replace(
    /\/$/,
    ''
  );
  const cdnBase = envBase || MEDIA_CDN_FALLBACK_URL.replace(/\/$/, '');
  const path = `media/${type}/${file}`;
  if (cdnBase) return `${cdnBase}/${path}`;
  return process.env.NODE_ENV !== 'production' ? `/${path}` : '';
}

/** Convenience accessor for the ambient background video on the assistant landing. */
export function getAmbientVideoUrl(): string {
  return getMediaUrl('video', 'aol-ambient.mp4');
}
